import type { TFunction } from 'i18next';
import { Copy, Download, Lock, Printer } from 'react-bootstrap-icons';

import { DataTestIds } from '@repo/frontend-shared';
import { SDSButton } from '@repo/sds/SDSButton';
import { SDSDialog } from '@repo/sds/SDSDialog';
import { SDSFlexbox } from '@repo/sds/SDSFlexbox';
import { SDSIcon } from '@repo/sds/SDSIcon';
import { SDSText } from '@repo/sds/SDSText';
import { toast } from '@repo/sds/utils/toast';
import { ClipboardCopyIconTooltip } from '@repo/ui/components/elements/ClipboardCopyIconTooltip/ClipboardCopyIconTooltip';

import { MfaSettingsPage } from './mfa-settings-page';

import './mfa-recovery-codes-panel.scss';

const MFA = DataTestIds.COMMON.SECURITY.MFA;

export type MfaRecoveryCodesVariant = 'save' | 'manage';

interface MfaRecoveryCodesPanelProps {
  codes: string[];
  isMobile: boolean;
  onClose: () => void;
  t: TFunction<'common'>;
  variant: MfaRecoveryCodesVariant;
}

function recoveryCodesText(codes: string[]): string {
  return codes.join('\n');
}

function escapeHtml(value: string): string {
  return value.replaceAll('&', '&amp;').replaceAll('<', '&lt;').replaceAll('>', '&gt;').replaceAll('"', '&quot;');
}

async function copyRecoveryCodes(codes: string[], copiedMessage: string, errorMessage: string): Promise<void> {
  try {
    await navigator.clipboard.writeText(recoveryCodesText(codes));
    toast.success(copiedMessage);
  } catch {
    toast.error(errorMessage);
  }
}

function downloadRecoveryCodes(codes: string[], filename: string): void {
  const blob = new Blob([recoveryCodesText(codes)], { type: 'text/plain' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}

function printRecoveryCodes(codes: string[], title: string): void {
  const popup = window.open('', '_blank', 'noopener,noreferrer');
  if (!popup) {
    return;
  }
  const body = codes.map((code, index) => `${index + 1}. ${code}`).join('\n');
  popup.document.write(`<!doctype html><title>${escapeHtml(title)}</title><pre>${escapeHtml(body)}</pre>`);
  popup.document.close();
  popup.focus();
  popup.print();
}

function RecoveryCodeRow({ code, index, t }: { code: string; index: number; t: TFunction<'common'> }) {
  const label = `${index}.`;
  return (
    <div className="MfaRecoveryCodes__row" data-testid={MFA.BACKUP_CODE}>
      <SDSText className="MfaRecoveryCodes__index" color="muted" text={label} type="body-medium" />
      <SDSText className="MfaRecoveryCodes__value" color="text-secondary" text={code} type="body-large" />
      <ClipboardCopyIconTooltip
        ariaLabel={t('security.mfa.backupCopyCodeAria', { index })}
        color="primary"
        copyErrorToastMessage={t('security.mfa.copyCodesError')}
        copiedTooltip={t('security.mfa.codesCopied')}
        idleTooltip={t('security.mfa.copyCodes')}
        size="small"
        textToCopy={code}
      />
    </div>
  );
}

function RecoveryCodeGrid({
  codes,
  singleColumn,
  t,
}: {
  codes: string[];
  singleColumn: boolean;
  t: TFunction<'common'>;
}) {
  if (singleColumn) {
    return (
      <div className="MfaRecoveryCodes__grid MfaRecoveryCodes__grid--single">
        {codes.map((code, index) => (
          <RecoveryCodeRow code={code} index={index + 1} key={code} t={t} />
        ))}
      </div>
    );
  }

  const midpoint = Math.ceil(codes.length / 2);
  const rows = Array.from({ length: midpoint }, (_, rowIndex) => {
    const left = codes[rowIndex];
    const right = codes[rowIndex + midpoint];
    return { left, right, rowIndex };
  });

  return (
    <div className="MfaRecoveryCodes__grid">
      {rows.map(row => (
        <div className="MfaRecoveryCodes__pair" key={`${row.left ?? 'empty'}-${row.right ?? 'empty'}`}>
          {row.left ? <RecoveryCodeRow code={row.left} index={row.rowIndex + 1} t={t} /> : null}
          {row.right ? <RecoveryCodeRow code={row.right} index={row.rowIndex + midpoint + 1} t={t} /> : null}
        </div>
      ))}
    </div>
  );
}

function RecoveryToolbar({ codes, t }: { codes: string[]; t: TFunction<'common'> }) {
  return (
    <SDSFlexbox className="MfaRecoveryCodes__toolbar" gap="button-large" justifyContent="flex-end">
      <SDSButton
        dataTestId={MFA.COPY_BACKUP}
        leftIcon={<SDSIcon color="text-secondary" icon={<Copy />} size="small" />}
        mode="ghost"
        onClick={() => void copyRecoveryCodes(codes, t('security.mfa.codesCopied'), t('security.mfa.copyCodesError'))}
        size="small"
        text={t('security.mfa.backupCopyAll')}
        textType="button-medium"
      />
      <SDSButton
        dataTestId={MFA.DOWNLOAD_BACKUP}
        leftIcon={<SDSIcon color="text-secondary" icon={<Download />} size="small" />}
        mode="ghost"
        onClick={() => downloadRecoveryCodes(codes, t('security.mfa.backupDownloadFilename'))}
        size="small"
        text={t('security.mfa.backupDownload')}
        textType="button-medium"
      />
      <SDSButton
        dataTestId={MFA.PRINT_BACKUP}
        leftIcon={<SDSIcon color="text-secondary" icon={<Printer />} size="small" />}
        mode="ghost"
        onClick={() => printRecoveryCodes(codes, t('security.mfa.backupSaveTitle'))}
        size="small"
        text={t('security.mfa.backupPrint')}
        textType="button-medium"
      />
    </SDSFlexbox>
  );
}

function RecoveryNote({ t }: { t: TFunction<'common'> }) {
  return (
    <SDSFlexbox alignItems="center" className="MfaRecoveryCodes__note" gap="text-small">
      <SDSIcon color="muted" icon={<Lock />} size="small" />
      <SDSText color="muted" text={t('security.mfa.backupKeepSafe')} type="body-small" />
    </SDSFlexbox>
  );
}

export function MfaRecoveryCodesPanel({ codes, isMobile, onClose, t, variant }: MfaRecoveryCodesPanelProps) {
  const isManage = variant === 'manage';
  const grid = <RecoveryCodeGrid codes={codes} singleColumn={isMobile} t={t} />;
  const body = (
    <SDSFlexbox className="MfaRecoveryCodes" direction="column" fullWidth gap="text-x-large">
      <SDSText text={t('security.mfa.backupSaveLead')} type="body-large" />
      {isManage ? <RecoveryToolbar codes={codes} t={t} /> : null}
      {grid}
      <RecoveryNote t={t} />
    </SDSFlexbox>
  );

  const saveActions = (
    <SDSFlexbox className="MfaRecoveryCodes__footerActions" gap="button-medium">
      <SDSButton
        dataTestId={MFA.COPY_BACKUP}
        isFullWidth={isMobile}
        mode="outline"
        onClick={() => void copyRecoveryCodes(codes, t('security.mfa.codesCopied'), t('security.mfa.copyCodesError'))}
        text={t('security.mfa.backupCopyAll')}
      />
      <SDSButton
        dataTestId={MFA.DOWNLOAD_BACKUP}
        isFullWidth={isMobile}
        onClick={() => downloadRecoveryCodes(codes, t('security.mfa.backupDownloadFilename'))}
        text={t('security.mfa.backupDownload')}
      />
    </SDSFlexbox>
  );

  const manageActions = <SDSButton dataTestId={MFA.CONFIRM} onClick={onClose} text={t('security.mfa.backupDone')} />;

  if (isMobile) {
    return (
      <MfaSettingsPage
        actions={
          <SDSFlexbox
            className={
              isManage ? 'MfaRecoveryCodes__footer' : 'MfaRecoveryCodes__footer MfaRecoveryCodes__footer--stack'
            }
            fullWidth
            gap="button-medium"
            justifyContent="flex-end"
          >
            {isManage ? manageActions : saveActions}
          </SDSFlexbox>
        }
        ariaLabel={t('security.mfa.backupSaveTitle')}
        backLabel={t('security.mfa.backToSecurity')}
        dataTestId={MFA.BACKUP_CODES}
        onBack={onClose}
      >
        <SDSText text={t('security.mfa.backupSaveTitle')} type="h4" />
        {body}
      </MfaSettingsPage>
    );
  }

  return (
    <SDSDialog
      className="MfaRecoveryCodesDialog"
      dataTestId={MFA.BACKUP_CODES}
      isOpen
      onClose={onClose}
      showCloseButton
      size="large"
      title={t('security.mfa.backupSaveTitle')}
    >
      {body}
      <SDSFlexbox className="MfaRecoveryCodes__footer" fullWidth gap="button-medium" justifyContent="flex-end">
        {isManage ? manageActions : saveActions}
      </SDSFlexbox>
    </SDSDialog>
  );
}
